import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Play, Pause, Trash2, Send, Loader2, Mail, MousePointerClick, Reply, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { engageApi } from "@/lib/engageApi";
import { useI18n } from "@/lib/i18n";

const STATUS_STYLE = {
    draft: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
    active: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    paused: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    completed: "bg-blue-500/15 text-blue-300 border-blue-500/30",
};

const EVENT_ICONS = {
    sent: { icon: Send, cls: "text-brand-indigo bg-brand-indigo/10" },
    open: { icon: Mail, cls: "text-emerald-300 bg-emerald-500/10" },
    click: { icon: MousePointerClick, cls: "text-brand-cyan bg-brand-cyan/10" },
    reply: { icon: Reply, cls: "text-brand-violet bg-brand-violet/10" },
    bounce: { icon: AlertTriangle, cls: "text-rose-300 bg-rose-500/10" },
};

export default function CampaignDetail() {
    const { campaignId } = useParams();
    const navigate = useNavigate();
    const { t } = useI18n();
    const [campaign, setCampaign] = useState(null);
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);

    useEffect(() => { load(); }, [campaignId]);

    async function load() {
        setLoading(true);
        try {
            const [c, ev] = await Promise.all([
                engageApi.campaigns.get(campaignId),
                engageApi.campaigns.events(campaignId, 100),
            ]);
            setCampaign(c);
            setEvents(ev);
        } catch (_e) { toast.error("Failed to load"); }
        finally { setLoading(false); }
    }

    async function launch() {
        setBusy(true);
        try {
            const res = await engageApi.campaigns.send(campaignId);
            toast.success(t.engage.campaigns.sentBanner(res.scheduled, res.simulated));
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Launch failed");
        } finally { setBusy(false); }
    }

    async function toggle(newStatus) {
        setBusy(true);
        try {
            await engageApi.campaigns.update(campaignId, { status: newStatus });
            await load();
        } catch (_e) { toast.error("Failed"); }
        finally { setBusy(false); }
    }

    async function handleDelete() {
        if (!window.confirm(`Delete campaign "${campaign.name}"?`)) return;
        try {
            await engageApi.campaigns.remove(campaignId);
            toast.success("Campaign deleted");
            navigate("/app/engage/campaigns");
        } catch (_e) { toast.error("Failed"); }
    }

    if (loading || !campaign) {
        return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 size={22} className="animate-spin text-zinc-500" /></div>;
    }

    const stats = campaign.stats || {};

    return (
        <div className="px-6 lg:px-10 py-6">
            <Link to="/app/engage/campaigns" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white transition" data-testid="campaign-detail-back">
                <ArrowLeft size={14} /> {t.engage.detail.back}
            </Link>

            {/* Header */}
            <div className="mt-4 flex flex-wrap items-start justify-between gap-4">
                <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                        <h1 className="font-display font-black text-3xl tracking-tighter" data-testid="campaign-detail-name">{campaign.name}</h1>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium border ${STATUS_STYLE[campaign.status]}`} data-testid="campaign-detail-status">
                            {t.engage.campaigns.statusLabels[campaign.status] || campaign.status}
                        </span>
                    </div>
                    <div className="mt-1 text-sm text-zinc-500">
                        {campaign.steps?.length || 0} steps · {campaign.lead_ids?.length || 0} leads · sender: {campaign.sender_name}
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {campaign.status === "draft" && (
                        <button type="button" onClick={launch} disabled={busy} data-testid="campaign-launch" className="btn-cta text-white font-semibold text-sm px-4 py-2 rounded-full inline-flex items-center gap-2 disabled:opacity-60">
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} {t.engage.detail.launch}
                        </button>
                    )}
                    {campaign.status === "active" && (
                        <button type="button" onClick={() => toggle("paused")} disabled={busy} data-testid="campaign-pause" className="text-sm px-4 py-2 rounded-full border border-white/12 hover:border-white/25 hover:bg-white/5 text-white inline-flex items-center gap-2">
                            <Pause size={14} /> {t.engage.detail.pause}
                        </button>
                    )}
                    {campaign.status === "paused" && (
                        <button type="button" onClick={() => toggle("active")} disabled={busy} data-testid="campaign-resume" className="btn-cta text-white font-semibold text-sm px-4 py-2 rounded-full inline-flex items-center gap-2">
                            <Play size={14} /> {t.engage.detail.resume}
                        </button>
                    )}
                    <button type="button" onClick={handleDelete} data-testid="campaign-delete" className="text-sm px-3 py-2 rounded-full border border-white/10 hover:border-rose-500/40 hover:bg-rose-500/5 text-rose-300 inline-flex items-center gap-2">
                        <Trash2 size={14} /> {t.engage.detail.delete}
                    </button>
                </div>
            </div>

            {/* Stats grid */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatCard label={t.engage.detail.stats.sent} value={stats.sent || 0} rate={null} testKey="sent" />
                <StatCard label={t.engage.detail.stats.open} value={stats.open || 0} rate={stats.open_rate} testKey="open" />
                <StatCard label={t.engage.detail.stats.click} value={stats.click || 0} rate={stats.click_rate} testKey="click" />
                <StatCard label={t.engage.detail.stats.reply} value={stats.reply || 0} rate={stats.reply_rate} testKey="reply" />
                <StatCard label={t.engage.detail.stats.bounce} value={stats.bounce || 0} rate={stats.bounce_rate} testKey="bounce" />
            </div>

            <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Sequence */}
                <div className="lg:col-span-2 space-y-3">
                    <div className="text-sm font-semibold text-white">{t.engage.detail.sequence}</div>
                    {(campaign.steps || []).map((s, i) => (
                        <div key={s.step_id} className="rounded-2xl p-5 bg-brand-surface border border-white/8" data-testid={`campaign-step-${i}`}>
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-indigo to-brand-violet text-white text-xs font-bold flex items-center justify-center">{i + 1}</div>
                                <div className="text-xs font-mono text-zinc-500">Day +{s.day_offset} · variant {s.variant}</div>
                            </div>
                            <div className="text-white font-medium mb-2">{s.subject}</div>
                            <pre className="text-sm text-zinc-300 whitespace-pre-wrap font-sans bg-brand-void/60 rounded-lg p-3 border border-white/5">{s.body}</pre>
                        </div>
                    ))}
                </div>

                {/* Events */}
                <div>
                    <div className="text-sm font-semibold text-white mb-3">{t.engage.detail.events}</div>
                    <div className="rounded-2xl bg-brand-surface border border-white/8 overflow-hidden">
                        {events.length === 0 ? (
                            <div className="p-6 text-center text-sm text-zinc-500" data-testid="campaign-events-empty">{t.engage.detail.empty}</div>
                        ) : (
                            <ul className="divide-y divide-white/5 max-h-[640px] overflow-y-auto">
                                {events.map((e) => {
                                    const cfg = EVENT_ICONS[e.type] || EVENT_ICONS.sent;
                                    const Icon = cfg.icon;
                                    return (
                                        <li key={e.event_id} className="px-4 py-3" data-testid={`event-${e.event_id}`}>
                                            <div className="flex items-start gap-2.5">
                                                <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${cfg.cls}`}>
                                                    <Icon size={13} />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <div className="text-sm text-white truncate">
                                                        <span className="capitalize font-medium">{e.type}</span>
                                                        <span className="text-zinc-500"> · </span>
                                                        <span className="text-zinc-300">{e.lead_name}</span>
                                                    </div>
                                                    <div className="text-xs text-zinc-500 truncate">{e.subject}</div>
                                                    {e.type === "reply" && (
                                                        <div className="mt-1 text-xs text-brand-cyan italic truncate">"{e.snippet}"</div>
                                                    )}
                                                    <div className="mt-1 text-[10px] font-mono text-zinc-600">{new Date(e.timestamp).toLocaleString()}</div>
                                                </div>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function StatCard({ label, value, rate, testKey }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-4 bg-brand-surface border border-white/8"
            data-testid={`campaign-stat-${testKey}`}
        >
            <div className="text-xs text-zinc-500">{label}</div>
            <div className="mt-1 font-display font-black text-2xl tracking-tighter">{value}</div>
            {rate != null && (
                <div className="mt-0.5 text-[11px] font-mono text-brand-cyan">{(rate * 100).toFixed(1)}%</div>
            )}
        </motion.div>
    );
}
